/**
 * Slide-in modal that renders a study sheet without leaving the chat.
 * Triggered by markdown links with `poker-trainer://sheet/<id>` href.
 *
 * Mirrors the iOS behavior: tapping a citation in chat presents the
 * sheet over whatever the user was doing; dismissing returns them to
 * exactly that spot.
 */

import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { fetchSheetContent, listSheets } from "@/shared/api";
import { ICON_STROKE, X } from "@/shared/components/icons";

interface SheetModalProps {
  sheetId: string;
  onClose: () => void;
}

export function SheetModal({ sheetId, onClose }: SheetModalProps) {
  const meta = useQuery({
    queryKey: ["sheets"],
    queryFn: listSheets,
    staleTime: Infinity,
  });
  const content = useQuery({
    queryKey: ["sheet", sheetId],
    queryFn: () => fetchSheetContent(sheetId),
    staleTime: Infinity,
  });

  // ESC closes the modal
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Lock scroll on the underlying chat
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const sheet = meta.data?.find((s) => s.id === sheetId);

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label={sheet?.displayName ?? "Study sheet"}
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close sheet"
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm cursor-default"
      />

      {/* Panel */}
      <aside
        className="relative w-full max-w-2xl h-full bg-bg border-l border-border overflow-y-auto scroll-momentum shadow-2xl"
        style={{
          animation: "sheet-slide-in 0.24s cubic-bezier(0.2, 0.9, 0.4, 1)",
        }}
      >
        <header className="sticky top-0 z-10 bg-bg/95 backdrop-blur border-b border-border px-6 py-4 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-text truncate">
              {sheet?.displayName ?? "Sheet"}
            </h2>
            {sheet && (
              <div className="text-xs text-text-muted mt-0.5">
                Sheet {sheet.number.toString().padStart(2, "0")} ·{" "}
                {sheet.category}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 w-8 h-8 rounded-lg border border-border text-text-muted hover:text-text hover:border-text-muted/40 transition flex items-center justify-center"
            aria-label="Close"
          >
            <X size={16} strokeWidth={ICON_STROKE} />
          </button>
        </header>

        <div className="px-6 py-6">
          {content.isLoading && (
            <div className="text-sm text-text-muted">Loading sheet…</div>
          )}
          {content.error && (
            <div className="text-sm text-danger">
              Couldn't load sheet:{" "}
              {content.error instanceof Error
                ? content.error.message
                : "Unknown error"}
            </div>
          )}
          {content.data && (
            <article className="prose-sheet">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content.data}
              </ReactMarkdown>
            </article>
          )}
        </div>
      </aside>

      <style>{`
        @keyframes sheet-slide-in {
          from { transform: translateX(24px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
